import { useState } from 'react'
import { useGetList, useNotify, useStore } from 'ra-core'
import { Button } from '@internal/ui-kit/components/ui/button'
import { Combobox } from '@internal/ui-kit/components/ui/combobox'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@internal/ui-kit/components/ui/drawer'
import { Input } from '@internal/ui-kit/components/ui/input'
import { Label } from '@internal/ui-kit/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@internal/ui-kit/components/ui/select'
import { specialistLabel } from '@/components/app/useSpecialists'
import type { SpecialistRecord } from '@/components/app/useSpecialists'
import { extractErrorMessage, httpClient } from '@internal/ui-kit/lib/httpClient'
import { useProductOptions, useSlimProducts } from './useUserProfileData'

const MAX_REMEMBERED_LOCATIONS = 10

// This form books an office-service appointment, not a branch visit — per
// ticket feedback ("branches selection can be removed actually - this form
// is used for office service") the location name/address fields are no
// longer sourced from the `branches` resource. Instead, whatever an admin
// types is remembered (`useStore`, the same localStorage-backed persistence
// `app-sidebar.tsx`'s category collapse state already uses, HATCH-111) so it
// resurfaces as a dropdown option on subsequent bookings, per "cache it for
// this session - so that this option(s) appear first in the drop down for
// subsequent events creations".
function rememberLocation(remembered: string[], value: string): string[] {
  if (!value) return remembered
  return [value, ...remembered.filter((entry) => entry !== value)].slice(0, MAX_REMEMBERED_LOCATIONS)
}

/**
 * `createEvent` (`retool-exports/user-profile/queries.json`, `drawerFrame1CreateEvent`
 * — "book a new appointment", per this ticket's own acceptance criteria: a
 * drawer with product/option/time/location selects, not the full form).
 * `select3FormSlug`/`select4PdfTemplate`/`select5LocationType`'s real static
 * choice lists (`retool-exports/user-profile/components.json`,
 * `_values`/`_labels`) — ticket feedback: these were shipped as fixed,
 * non-user-facing values, but the source export's widgets do have real
 * static dropdowns, just not a dynamic `data` query to build them from.
 */
const FORM_SLUG_OPTIONS = [
  { value: 'vaccination-certificate', label: 'Vaccination Cetificate' },
  { value: 'post-consultation', label: 'Post Consultation' },
]
const PDF_TEMPLATE_OPTIONS = [
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'consultation', label: 'Consultation' },
]
const LOCATION_TYPE_OPTIONS = [
  { value: 'by_appointment', label: 'By appointment' },
  { value: 'online', label: 'Online' },
  { value: 'walk_in', label: 'Walk-in' },
]

// Single `type="datetime-local"` fields, not split date + time inputs — round-7
// feedback ("Still don't see date/time pickers. Can we use just single datetime
// prickers instead (since anyways current approach doesn't work)").
const DATE_TIME_PICKER_CLASS = 'scheme-light dark:scheme-dark [&::-webkit-calendar-picker-indicator]:cursor-pointer'

function dateTimeForInput(date: Date): string {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`
}

function nowDateTimeForInput(): string {
  return dateTimeForInput(new Date())
}

const DEFAULT_DURATION_MINUTES = 30

function defaultEndDateTimeForInput(): string {
  return dateTimeForInput(new Date(Date.now() + DEFAULT_DURATION_MINUTES * 60 * 1000))
}

export function CreateEventDrawer({ userId, onCreated }: { userId: string; onCreated: () => void }) {
  const [open, setOpen] = useState(false)
  const [productId, setProductId] = useState<string>()
  const [optionId, setOptionId] = useState<string>()
  const [ownerId, setOwnerId] = useState<string>()
  const [formSlug, setFormSlug] = useState(FORM_SLUG_OPTIONS[0].value)
  const [pdfTemplateType, setPdfTemplateType] = useState(PDF_TEMPLATE_OPTIONS[0].value)
  const [locationType, setLocationType] = useState(LOCATION_TYPE_OPTIONS[0].value)
  const [locationName, setLocationName] = useState('')
  const [locationAddress, setLocationAddress] = useState('')
  const [startDateTime, setStartDateTime] = useState(nowDateTimeForInput())
  const [endDateTime, setEndDateTime] = useState(defaultEndDateTimeForInput())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const notify = useNotify()

  const { products } = useSlimProducts()
  const { options } = useProductOptions(productId)
  const { data: specialists } = useGetList<SpecialistRecord>(
    'specialists',
    { pagination: { page: 1, perPage: 100 }, sort: { field: 'first_name', order: 'ASC' }, filter: {} },
    { enabled: open },
  )
  const specialistOptions = (specialists ?? []).map((specialist) => ({
    value: String(specialist.id),
    label: specialistLabel(specialist),
  }))
  const [rememberedLocationNames, setRememberedLocationNames] = useStore<string[]>(
    'users.createEvent.recentLocationNames',
    [],
  )
  const [rememberedLocationAddresses, setRememberedLocationAddresses] = useStore<string[]>(
    'users.createEvent.recentLocationAddresses',
    [],
  )

  const selectedProduct = products.find((product) => product.id === productId)
  const selectedOption = options.find((option) => option.option_id === optionId)
  const productOptions = products.map((product) => ({ value: String(product.id), label: product.title ?? product.id }))

  // Independent free-text-capable dropdowns (not one branch picker), each
  // offering whichever values this admin has typed before, most recent first.
  const locationNameOptions = rememberedLocationNames.map((name) => ({ value: name, label: name }))
  const locationAddressOptions = rememberedLocationAddresses.map((address) => ({ value: address, label: address }))

  const isEndBeforeStart = Boolean(
    startDateTime && endDateTime && new Date(endDateTime).getTime() <= new Date(startDateTime).getTime(),
  )

  const canSubmit = Boolean(
    productId &&
      optionId &&
      ownerId &&
      locationName &&
      locationAddress &&
      startDateTime &&
      endDateTime &&
      !isEndBeforeStart,
  )

  const reset = () => {
    setProductId(undefined)
    setOptionId(undefined)
    setOwnerId(undefined)
    setFormSlug(FORM_SLUG_OPTIONS[0].value)
    setPdfTemplateType(PDF_TEMPLATE_OPTIONS[0].value)
    setLocationType(LOCATION_TYPE_OPTIONS[0].value)
    setLocationName('')
    setLocationAddress('')
    setStartDateTime(nowDateTimeForInput())
    setEndDateTime(defaultEndDateTimeForInput())
  }

  const submit = async () => {
    if (!selectedProduct || !selectedOption) return
    setIsSubmitting(true)
    try {
      await httpClient('/events', {
        method: 'POST',
        body: JSON.stringify({
          form_slug: formSlug,
          pdf_template_type: pdfTemplateType,
          location_type: locationType,
          start_time: new Date(startDateTime).toISOString(),
          end_time: new Date(endDateTime).toISOString(),
          location_name: locationName,
          location_address: locationAddress,
          product_id: selectedProduct.id,
          product_option_id: selectedOption.option_id,
          owner_id: ownerId,
          title: selectedProduct.title,
          description: selectedOption.brand?.name ? `Provided By ${selectedOption.brand.name}` : undefined,
          participant_id: userId,
        }),
      })
      setRememberedLocationNames((remembered) => rememberLocation(remembered, locationName))
      setRememberedLocationAddresses((remembered) => rememberLocation(remembered, locationAddress))
      notify('Appointment booked', { type: 'success' })
      reset()
      setOpen(false)
      onCreated()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not book this appointment.'), { type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Drawer open={open} onOpenChange={setOpen} direction="right">
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        Book appointment
      </Button>
      <DrawerContent className="w-full sm:max-w-md">
        <DrawerHeader>
          <DrawerTitle>Book a new appointment</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-1.5">
            <Label>Choose Product</Label>
            <Combobox
              options={productOptions}
              value={productId}
              onChange={(value) => {
                setProductId(value)
                setOptionId(undefined)
              }}
              placeholder="Select an option"
              emptyText="No matching product found."
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Choose Option</Label>
            <Select value={optionId} onValueChange={setOptionId} disabled={!productId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.option_id} value={option.option_id}>
                    {option.title ?? option.option_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Specialist</Label>
            <Combobox
              options={specialistOptions}
              value={ownerId}
              onChange={setOwnerId}
              placeholder="Choose a specialist"
              emptyText="No matching specialist found."
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="event-start">Start</Label>
            {/* Root cause of rounds 6-8's "picker doesn't open" (vaul/vaul#391,
                shadcn-ui/ui#9578): vaul's own onPointerDown on the drawer
                content calls setPointerCapture for its drag gesture, which
                steals the pointer before Chromium can open the native
                calendar — no CSS fix (color-scheme/appearance) can work
                around that. Stopping propagation here keeps drag working on
                the rest of the drawer while letting the input open normally. */}
            <Input
              id="event-start"
              type="datetime-local"
              value={startDateTime}
              onChange={(event) => setStartDateTime(event.target.value)}
              onPointerDown={(event) => event.stopPropagation()}
              className={DATE_TIME_PICKER_CLASS}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="event-end">End</Label>
            <Input
              id="event-end"
              type="datetime-local"
              value={endDateTime}
              onChange={(event) => setEndDateTime(event.target.value)}
              onPointerDown={(event) => event.stopPropagation()}
              className={DATE_TIME_PICKER_CLASS}
            />
            {isEndBeforeStart && <p className="text-destructive text-sm">End must be after start.</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Location Name</Label>
            <Combobox
              options={locationNameOptions}
              value={locationName}
              onChange={setLocationName}
              placeholder="Select or type a location name"
              allowCustomValue
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Location Address</Label>
            <Combobox
              options={locationAddressOptions}
              value={locationAddress}
              onChange={setLocationAddress}
              placeholder="Select or type an address"
              allowCustomValue
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Form</Label>
            <Select value={formSlug} onValueChange={setFormSlug}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORM_SLUG_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>PDF Template type</Label>
            <Select value={pdfTemplateType} onValueChange={setPdfTemplateType}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PDF_TEMPLATE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Location Type</Label>
            <Select value={locationType} onValueChange={setLocationType}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCATION_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DrawerFooter>
          <Button type="button" disabled={!canSubmit || isSubmitting} onClick={submit}>
            Create Event
          </Button>
          <DrawerClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
